import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheck, 
  faTimes, 
  faQuestion, 
  faEllipsis, 
  faUtensils, 
  faBus,
  faEye,
  faEyeSlash,
  faChild,
  faUserGraduate,
  faBaby,
  faStickyNote,
  faEnvelope,
  faPhone,
  faLink,
  faCamera
} from '@fortawesome/free-solid-svg-icons';
import { 
  faInstagram, 
  faSpotify, 
  faFacebook, 
  faYoutube, 
  faTwitter, 
  faTiktok, 
  faLinkedin, 
  faPinterest 
} from '@fortawesome/free-brands-svg-icons';
import React from 'react';
import './iconStyles.css';

/**
 * Central file for defining all Font Awesome icons used in the application
 * This ensures consistency across components and makes future updates easier
 * Includes status icons, platform icons, and helper functions
 */

// Status icons
export const ATTENDING_ICON = <FontAwesomeIcon icon={faCheck} className="status-icon attending" />;
export const NOT_ATTENDING_ICON = <FontAwesomeIcon icon={faTimes} className="status-icon not-attending" />;
export const MAYBE_ICON = <FontAwesomeIcon icon={faQuestion} className="status-icon maybe" />;
export const MIXED_ICON = <FontAwesomeIcon icon={faEllipsis} className="status-icon mixed" />;

// View status icons
export const VIEWED_ICON = <FontAwesomeIcon icon={faEye} className="status-icon viewed" />;
export const NOT_VIEWED_ICON = <FontAwesomeIcon icon={faEyeSlash} className="status-icon not-viewed" />;

// Requirement icons
export const DIETARY_ICON = <FontAwesomeIcon icon={faUtensils} className="status-icon dietary" />;
export const TRANSPORTATION_ICON = <FontAwesomeIcon icon={faBus} className="status-icon transportation" />;
export const NOTES_ICON = <FontAwesomeIcon icon={faStickyNote} className="status-icon notes" />;

// Age category icons
export const TEENAGER_ICON = <FontAwesomeIcon icon={faUserGraduate} className="status-icon teenager" />;
export const CHILD_ICON = <FontAwesomeIcon icon={faChild} className="status-icon child" />;
export const BABY_ICON = <FontAwesomeIcon icon={faBaby} className="status-icon baby" />;

// Platform icons for links
export const INSTAGRAM_ICON = faInstagram;
export const SPOTIFY_ICON = faSpotify;
export const PHOTOS_ICON = faCamera;
export const FACEBOOK_ICON = faFacebook;
export const YOUTUBE_ICON = faYoutube;
export const TWITTER_ICON = faTwitter;
export const TIKTOK_ICON = faTiktok;
export const LINKEDIN_ICON = faLinkedin;
export const PINTEREST_ICON = faPinterest;
export const EMAIL_ICON = faEnvelope;
export const PHONE_ICON = faPhone;
export const LINK_ICON = faLink;

/**
 * Helper function to get status icon based on status string
 * @param {string|boolean} status - The status string (ATTENDING, NOT_ATTENDING, MAYBE, MIXED) or boolean
 * @returns {JSX.Element} - The corresponding Font Awesome icon
 */
export const getStatusIcon = (status) => {
  // Handle boolean values
  if (status === true) {
    return ATTENDING_ICON;
  }
  if (status === false) {
    return NOT_ATTENDING_ICON;
  }
  
  // Handle string values
  switch (status) {
    case 'ATTENDING': return ATTENDING_ICON;
    case 'NOT_ATTENDING': return NOT_ATTENDING_ICON;
    case 'MAYBE': return MAYBE_ICON;
    case 'MIXED': return MIXED_ICON;
    default: return null;
  }
};

/**
 * Helper function to get view status icon
 * @param {boolean} viewed - Whether the invitation has been viewed
 * @returns {JSX.Element} - The corresponding Font Awesome icon
 */
export const getViewStatusIcon = (status) => {
  // Handle boolean values
  if (status === true) {
    return VIEWED_ICON;
  }
  if (status === false) {
    return NOT_VIEWED_ICON;
  }
  
  // Handle string values
  switch (status) {
    case 'VIEWED': return VIEWED_ICON;
    case 'NOT_VIEWED': return NOT_VIEWED_ICON;
    default: return null;
  }

};

/**
 * Helper function to get dietary requirements icon
 * @param {Object} rsvp - The rsvp object
 * @param {Function} isValidDietaryRequirement - Function to validate dietary requirements
 * @returns {JSX.Element|null} - The dietary requirements icon or null
 */
export const getDietaryIcon = (rsvp, isValidDietaryRequirement) => {
  return rsvp.dietaryRequirements && isValidDietaryRequirement(rsvp.dietaryRequirements) 
    ? DIETARY_ICON 
    : null;
};

/**
 * Helper function to check if additional notes is valid (not "No", "None", etc.)
 * @param {string} notes - The additional notes string
 * @returns {boolean} - Whether the notes are valid
 */
const isValidAdditionalNotes = (notes) => {
  if (!notes) return false;
  
  const notes_lower = notes.toLowerCase().trim();
  const negative_values = ['no', 'none', 'n/a', 'na', '-', 'nil', 'null', 'nope'];
  
  return !negative_values.includes(notes_lower);
};

/**
 * Helper function to get additional notes icon
 * @param {Object} rsvp - The rsvp object
 * @returns {JSX.Element|null} - The additional notes icon or null
 */
export const getNotesIcon = (rsvp) => {
  return getAdditionalNotesIcon(rsvp, isValidAdditionalNotes);
};

/**
 * Helper function to get transportation icon
 * @param {Object} rsvp - The rsvp object
 * @returns {JSX.Element|null} - The transportation icon or null
 */
// TODO validate whether this is stored as a bool or a string, i.e. will false actually equal true?
export const getTransportationIcon = (rsvp) => {
  return rsvp.additionalOptions && rsvp.additionalOptions !== "false" ? TRANSPORTATION_ICON : null;
};

/**
 * Helper function to get age category icon
 * @param {Object} rsvp - The rsvp object
 * @returns {JSX.Element|null} - The age category icon or null
 */
export const getAgeCategoryIcon = (rsvp) => {
  if (!rsvp?.ageCategory) return null;
  
  switch (rsvp.ageCategory) {
    case 'Teenager': return TEENAGER_ICON;
    case 'Child': return CHILD_ICON;
    case 'Baby': return BABY_ICON;
    default: return null; // Adult gets no icon
  }
};

/**
 * Helper function to get additional notes icon
 * @param {Object} rsvp - The rsvp object
 * @param {Function} isValidAdditionalNotes - Function to validate additional notes
 * @returns {JSX.Element|null} - The additional notes icon or null
 */
export const getAdditionalNotesIcon = (rsvp, isValidAdditionalNotes) => {
  return rsvp.additionalNotes && isValidAdditionalNotes(rsvp.additionalNotes) 
    ? NOTES_ICON 
    : null;
};

/**
 * Helper function to get platform icon for URLs
 * @param {string} url - The URL to get icon for
 * @returns {Object} - The FontAwesome icon object
 */
export const getPlatformIcon = (url) => {
  if (!url) return LINK_ICON;
  
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname.toLowerCase().replace(/^www\./, '');
    
    if (hostname === 'instagram.com' || hostname.endsWith('.instagram.com')) return INSTAGRAM_ICON;
    if (hostname === 'spotify.com' || hostname.endsWith('.spotify.com')) return SPOTIFY_ICON;
    if (hostname === 'photos.google.com' || hostname.endsWith('.photos.google.com') || hostname === 'photos.app.goo.gl') return PHOTOS_ICON;
    if (hostname === 'facebook.com' || hostname.endsWith('.facebook.com')) return FACEBOOK_ICON;
    if (hostname === 'youtube.com' || hostname.endsWith('.youtube.com') || hostname === 'youtu.be') return YOUTUBE_ICON;
    if (hostname === 'twitter.com' || hostname.endsWith('.twitter.com') || hostname === 'x.com' || hostname.endsWith('.x.com')) return TWITTER_ICON;
    if (hostname === 'tiktok.com' || hostname.endsWith('.tiktok.com')) return TIKTOK_ICON;
    if (hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com')) return LINKEDIN_ICON;
    if (hostname === 'pinterest.com' || hostname.endsWith('.pinterest.com')) return PINTEREST_ICON;
  } catch {
    if (url.startsWith('mailto:')) return EMAIL_ICON;
    if (url.startsWith('tel:')) return PHONE_ICON;
  }
  
  return LINK_ICON;
};


